import React from "react";
import { rendTitle } from '@sg/utils/commonPageMethod';
import SpecialTableBar from '@sg/components/CapsuleBar/SpecialTableBar';
import { ICapsuleData } from "@sg/components/CapsuleBar/type.d";
import { getTableCapsuleConfig } from '@sg/utils/business';
import { IntlShape } from 'react-intl';
import { localizeLabel } from '@sg/utils/translate';
import SvgIcon from '@/pages/home/components/SvgIcon';
import Narrow from '@sg/assests/images/Narrow.svg';
import { TABLE_VS_FIELD } from '../config';
import { mathFormat, getYearPrefix } from '@sg/utils/common';
import { toMillions } from '@/pages/strategic/utils/common';
import { renderNewlineToBr } from '@sg/utils/commonPageMethod';
import ExpandCollapseButton from '@sg/components/ExpandCollapseButton';
import EditableField from "@sg/components/EditableField";
import PhaseCell from "@sg/components/PhaseCell";
import FormatHtmlTextToReact from '@home/components/FormatHtmlTextToReact';
import { createBtnPoint, SGBuriedBtnPoint, OverviewTab } from '@/pages/strategic/utils/buriedPoint';
import './pageConfig.scss';

export const DefaultChat = {
    icon: "discount-chart",
    value: "line",
    tip: "Line chart",
    format: true,
};

// Revenue by Projects table
export const projectColums = (
    maxVsVallue: number,
    formatMessage: IntlShape['formatMessage'], 
    projectsUpdateTime?: string,
    options?: { isAllExpanded?: boolean; onToggleExpandAll?: () => void }
) => {
    const yearPrefix = getYearPrefix(projectsUpdateTime);

    return [
    {
        header: (
            <div>
                {options?.onToggleExpandAll ? (
                    <ExpandCollapseButton
                        isExpanded={!!options.isAllExpanded}
                        onClick={() => options.onToggleExpandAll!()}
                        onTrackToggle={(nextExpanded) => {
                            const btnPoint = createBtnPoint(
                                OverviewTab,
                                'grossRevenueByProjectsCollapse',
                                nextExpanded
                                    ? 'Gross Revenue by Projects Expand All'
                                    : 'Gross Revenue by Projects Collapse All',
                            );
                            if (btnPoint) {
                                SGBuriedBtnPoint(btnPoint);
                            }
                        }}
                        title={formatMessage({ id: 'dbs_project' })}
                    />
                ) : (
                    <span style={{ fontWeight: 700 }}>
                        {formatMessage({ id: 'dbs_project' })}
                    </span>
                )}
            </div>
        ),
        dataIndex: 'Project',
        key: 'Project',
        width: 120,
        render: (cvm: any) => <span style={{ display: 'inline-block', fontWeight: cvm.isCategory ? 700 : 400, paddingLeft: cvm.isCategory ? '' : '22px' }}>{localizeLabel(cvm?.Project, formatMessage)}</span>
    },
    {
        header: rendTitle({ title: `${yearPrefix}A`, tips: '(M$)', titleSize: '14px', tipSize: '12px', align: 'center' }),
        dataIndex: '25A',
        key: '25A',
        width: 70,
        render: (cvm: any) => (
            <div style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>
                {toMillions(cvm?.['25A'])}
            </div>
        )
    },
    {
        header: rendTitle({ title: `${yearPrefix}F`, tips: '(M$)', titleSize: '14px', tipSize: '12px', align: 'center' }),
        dataIndex: '25F',
        key: '25F',
        width: 70,
        render: (cvm: any) => (
            <div style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>
                {toMillions(cvm?.['25F'])}
            </div>
        )
    },
    {
        header: rendTitle({ title: `${yearPrefix}B`, tips: '(M$)', titleSize: '14px', tipSize: '12px', align: 'center' }),
        dataIndex: '25B',
        key: '25B',
        width: 70,
        render: (cvm: any) => (
            <div style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>
                {toMillions(cvm?.['25B'])}
            </div>
        )
    },
    {
        header: <div className='dbs-table-25FVS25B'>{rendTitle({ title: `${yearPrefix}F VS ${yearPrefix}B`, tips: '(M$)', titleSize: '14px', tipSize: '12px' })}</div>,
        dataIndex: '25F VS 25B',
        key: '25F VS 25B',
        width: 100,
        render: (cvm: any) => {
            try {
                const dValue = Number((cvm?.[TABLE_VS_FIELD[0]] - cvm?.[TABLE_VS_FIELD[1]])?.toFixed(2)) || 0;
                if (dValue >= 0) {
                    return null;
                }
                const newArr: ICapsuleData[] = getTableCapsuleConfig({ value: dValue / 1000000, maxValue: maxVsVallue / 1000000 });
                return <div className='dbs-table-capsule dbs-table-capsule-negative'>
                    <SpecialTableBar data={newArr} height={20} coefficient={1.5} />
                </div>;
            } catch (error) {
                console.error(error);
            }
        }
    },
    {
        header: '',
        dataIndex: '25F VS 25B2',
        key: '25F VS 25B2',
        width: 100,
        render: (cvm: any) => {
            try {
                const dValue = Number((cvm?.[TABLE_VS_FIELD[0]] - cvm?.[TABLE_VS_FIELD[1]])?.toFixed(2)) || 0;
                if (dValue < 0) {
                    return null;
                }
                const newArr: ICapsuleData[] = getTableCapsuleConfig({ value: dValue / 1000000, maxValue: maxVsVallue / 1000000 });
                return <div
                    className='dbs-table-capsule dbs-table-capsule-positive'
                    style={{ marginLeft: dValue === 0 ? '0' : '-20px' }}
                >
                    <SpecialTableBar data={newArr} height={20} coefficient={1.5} />
                </div>;
            } catch (error) {
                console.error(error);
            }
        }
    },
    {
        header: formatMessage({ id: 'dbs_opportunities_risks' }),
        dataIndex: 'Opportunities & Risks',
        key: 'Opportunities & Risks',
        width: 140,
        render: (cvm: any) => <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            {cvm?.['Opportunities & Risks'] && <span style={{ fontSize: '12px' }}>
                {((cvm['25F'] - cvm['25B']) > 0 ?
                    <SvgIcon style={{ color: '#69D19C', position: 'relative', top: '-1px' }} type={'cross'} /> :
                    (cvm['25F'] - cvm['25B']) === 0 ? '' : <img width="12px" src={Narrow} alt="" style={{ position: 'relative', top: '1px' }} />)}
            </span>}
            <span>
                {renderNewlineToBr(cvm?.['Opportunities & Risks'])}
            </span>
        </div>,
    },
    {
        header: formatMessage({ id: 'dbs_action_note' }),
        dataIndex: 'Action & Note',
        key: 'Action & Note',
        width: 100,
        render: (cvm: any) => <div>{renderNewlineToBr(cvm?.['Action & Note'])}</div>
    }
];
};

export const editProjectColums = (maxVsVallue: number, formatMessage: IntlShape['formatMessage'], projectsUpdateTime?: string) => {
    const yearPrefix = getYearPrefix(projectsUpdateTime);

    return [
    {
        header: <div style={{ fontWeight: 700 }}>{formatMessage({ id: 'dbs_project' })}</div>,
        dataIndex: 'Project',
        key: 'Project',
        width: 120,
        // render: (cvm: any) => <span style={{ fontWeight: cvm.children ? 700 : 400, marginLeft: cvm.children ? '-11px' : '11px' }}> {localizeLabel(cvm?.Project, formatMessage)}</span>
        render: (cvm: any, rowKey: string, recordIndex: number, column: any, columnIndex: number, editData: any, stableUpdater: (value: any) => void, setEditData: any, validationErrors: Record<string, string[]>) => {
            // 判断是否为一级（category）：使用 isCategory 标识
            const isCategory = cvm.isCategory === true;

            const onChange = (value: any) => {
                stableUpdater(value);
            }

            const onDelete = () => {
                // 通过stableUpdater传递特殊标记来表示删除
                stableUpdater({ __delete: true });
            }

            // 判断是否有children（项目）
            const hasChildren = cvm.children && Array.isArray(cvm.children) && cvm.children.length > 0;

            // 配置删除图标：有children时置灰并显示提示，没有children时正常显示
            const showDeleteConfig = hasChildren
                ? { disabled: true, tooltip: formatMessage({ id: 'dbs_cannot_delete_category_while_contains_projects' }) }
                : { disabled: false };

            // 判断是否是新增行（id 以 'new-category-' 开头）
            const isNewRow = cvm.id && String(cvm.id).startsWith('new_project_');

            // 检查是否有错误（参考 editPointsColums）
            const recordId = String(cvm.id);
            const recordErrors = validationErrors[recordId] || [];
            const hasError = recordErrors.includes('Project');

            return (
                <span>
                    <span style={{ fontWeight: cvm.isCategory ? 700 : 400 }}>
                        {/* translate happen here */}
                        {/* 根据是否有children判断：有children显示PhaseCell，没有children显示普通文本 */}
                        {cvm.children ? (
                            <PhaseCell
                                titleText={localizeLabel(cvm.Project, formatMessage)}
                                onChange={onChange}
                                onDelete={onDelete}
                                showDelete={showDeleteConfig}
                                formatMessage={formatMessage}
                                isNewRow={isNewRow}
                                hasError={hasError}
                                deleteMessage={formatMessage({ id: 'dbs_cannot_delete_category_with_projects' })}
                            />
                        ) : (
                            <span>{localizeLabel(cvm.Project, formatMessage)}</span>
                        )}
                    </span>
                    {cvm.Project && cvm.Studio && '/'}
                    <span style={{ fontWeight: 400 }}>{cvm.Studio}</span>
                </span>
            )
        }
    },
    {
        header: rendTitle({ title: `${yearPrefix}A`, tips: '(M$)', titleSize: '14px', tipSize: '12px', align: 'center' }),
        dataIndex: '25A',
        key: '25A',
        width: 70,
        render: (cvm: any) => (
            <div style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>
                {toMillions(cvm?.['25A'])}
            </div>
        )
    },
    {
        header: rendTitle({ title: `${yearPrefix}F`, tips: '(M$)', titleSize: '14px', tipSize: '12px', align: 'center' }),
        dataIndex: '25F',
        key: '25F',
        width: 70,
        render: (cvm: any) => (
            <div style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>
                {toMillions(cvm?.['25F'])}
            </div>
        )
    },
    {
        header: rendTitle({ title: `${yearPrefix}B`, tips: '(M$)', titleSize: '14px', tipSize: '12px', align: 'center' }),
        dataIndex: '25B',
        key: '25B',
        width: 70,
        render: (cvm: any) => (
            <div style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>
                {toMillions(cvm?.['25B'])}
            </div>
        )
    },
    {
        header: <div className='dbs-table-25FVS25B'>{rendTitle({ title: `${yearPrefix}F VS ${yearPrefix}B`, tips: '(M$)', titleSize: '14px', tipSize: '12px' })}</div>,
        dataIndex: '25F VS 25B',
        key: '25F VS 25B',
        width: 100,
        render: (cvm: any) => {
            try {
                const dValue = Number((cvm?.[TABLE_VS_FIELD[0]] - cvm?.[TABLE_VS_FIELD[1]])?.toFixed(2)) || 0;
                if (dValue >= 0) {
                    return null;
                }
                const newArr: ICapsuleData[] = getTableCapsuleConfig({ value: dValue / 1000000, maxValue: maxVsVallue / 1000000 });
                return <div className='dbs-table-capsule dbs-table-capsule-negative'>
                    <SpecialTableBar data={newArr} height={20} coefficient={1.5} />
                </div>;
            } catch (error) {
                console.error(error);
            }
        }
    },
    {
        header: '',
        dataIndex: '25F VS 25B2',
        key: '25F VS 25B2',
        width: 100,
        render: (cvm: any) => {
            try {
                const dValue = Number((cvm?.[TABLE_VS_FIELD[0]] - cvm?.[TABLE_VS_FIELD[1]])?.toFixed(2)) || 0;
                if (dValue < 0) {
                    return null;
                }
                const newArr: ICapsuleData[] = getTableCapsuleConfig({ value: dValue / 1000000, maxValue: maxVsVallue / 1000000 });
                return <div
                    className='dbs-table-capsule dbs-table-capsule-positive'
                    style={{ marginLeft: dValue === 0 ? '0' : '-20px' }}
                >
                    <SpecialTableBar data={newArr} height={20} coefficient={1.5} />
                </div>;
            } catch (error) {
                console.error(error);
            }
        }
    },
    {
        header: formatMessage({ id: 'dbs_opportunities_risks' }),
        dataIndex: 'Opportunities & Risks',
        key: 'Opportunities & Risks',
        width: 140,
        render: (cvm: any, rowKey: string, recordIndex: number, column: any, columnIndex: number, editData: any, stableUpdater: (value: any) => void) => {
            const onChange = (value: any) => {
                stableUpdater(value);
            }
            return (
                <EditableField initialData={cvm} renderStyle={column.key} onChange={onChange} />
            )
        }
    },
    {
        header: formatMessage({ id: 'dbs_action_note' }),
        dataIndex: 'Action & Note',
        key: 'Action & Note',
        width: 100,
        render: (cvm: any, rowKey: string, recordIndex: number, column: any, columnIndex: number, editData: any, stableUpdater: (value: any) => void) => {
            const onChange = (value: any) => {
                stableUpdater(value);
            }
            return (
                <EditableField initialData={cvm} renderStyle={column.key} onChange={onChange} />
            )
        }
    }
];
};

// Gross Revenue Trend table columns
export const revenueTrendColumns = () => [
    {
        header: 'Date',
        dataIndex: 'date',
        key: 'date',
        width: 100,
    },
    {
        header: 'Monthly Actual',
        dataIndex: 'Actuals',
        key: 'Actuals',
        width: 100,
        format: mathFormat,
        render: (item: any) => <div style={{ textAlign: 'right', width: '105px', textWrap: 'nowrap' }}>{mathFormat(item?.Actuals) || '-'}</div>
    },
    {
        header: 'Monthly Forecast',
        dataIndex: 'Forecasts',
        key: 'Forecasts',
        width: 100,
        format: mathFormat,
        render: (item: any) => <div style={{ textAlign: 'right', width: '116px', textWrap: 'nowrap' }}>{mathFormat(item?.Forecasts) || '-'}</div>
    },
    {
        header: 'Monthly KPI',
        dataIndex: 'KPIs',
        key: 'KPIs',
        width: 100,
        format: mathFormat,
        render: (item: any) => <div style={{ textAlign: 'right', width: '87px', textWrap: 'nowrap' }}>{mathFormat(item?.KPIs) || '-'}</div>
    }
];

